<template>
    <b-modal :id="modalId" title="Keyboard Shortcuts" :size="groupedHotkeys.length > 1 ? 'xl' : null" scrollable>
        <div class="hotkey-discovery-content">
            <div v-if="!registeredHotkeys.length" class="text-center text-muted py-3">
                No keyboard shortcuts available on this page
            </div>
            <div v-else>
                <b-row>
                    <div :class="{ 'hotkey-section mb-2': true, 'col-lg-6 col-12': groupedHotkeys.length > 1, 'col-12': groupedHotkeys.length === 1 }"
                        v-for="section in groupedHotkeys" :key="section.category">
                        <h6 class="section-title mb-0">{{ section.category }}</h6>
                        <table class="table">
                            <tbody>
                                <tr v-for="hotkey in section.items" :key="hotkey.key">
                                    <td class="hotkey-key-cell">
                                        <kbd class="hotkey-badge my-2">{{ hotkey.displayKey }}</kbd>
                                    </td>
                                    <td class="hotkey-description">{{ hotkey.description }}</td>
                                    <td class="hotkey-scope text-muted">
                                        <small>{{ hotkey.scope }}</small>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </b-row>
            </div>
        </div>
        <template #modal-footer>
            <div class="mt-3 pt-3 border-top w-100">
                <p class="text-muted mb-0">
                    <small>
                        <strong>Tip:</strong> Press <kbd>?</kbd> or
                        <kbd>Shift+/</kbd> at any time to show this help dialog
                    </small>
                </p>
            </div>
        </template>
    </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';

interface Hotkey {
    key: string;
    displayKey: string;
    description: string;
    scope: string;
    category: string;
    handler?: Function;
}

interface HotkeySection {
    category: string;
    items: Hotkey[];
}

@Component
export default class HotkeyDiscoveryModal extends Vue {
    @Prop({ default: 'hotkey-discovery-modal' }) modalId!: string;

    private registeredHotkeys: Hotkey[] = [];
    private globalHotkeys: Hotkey[] = [
        {
            key: 'F2',
            displayKey: 'F2',
            description: 'Focus global search',
            scope: 'Global',
            category: 'Navigation',
        },
        {
            key: 'F4',
            displayKey: 'F4',
            description: 'Show recently viewed patients',
            scope: 'Global',
            category: 'Navigation',
        },
        {
            key: 'F8',
            displayKey: 'F8',
            description: 'Show history modal',
            scope: 'Pages with history',
            category: 'Navigation',
        },
        // {
        //     key: '?',
        //     displayKey: '? (Shift+/)',
        //     description: 'Show keyboard shortcuts help',
        //     scope: 'Global',
        //     category: 'Help',
        // },
    ];

    get verificationHotkeys(): Hotkey[] {
        return [
            {
                key: 'F5',
                displayKey: 'F5',
                description: 'Open prescription history',
                scope: 'Prescription Details',
                category: 'Page Actions',
            },
            {
                key: 'Ctrl+P',
                displayKey: 'Ctrl+P',
                description: 'Show patient notes modal',
                scope: 'Patient Notes',
                category: 'Page Actions',
            },
            {
                key: 'Ctrl+D',
                displayKey: 'Ctrl+D',
                description: 'Show doctor notes modal',
                scope: 'Doctor Notes',
                category: 'Page Actions',
            },
            {
                key: 'Ctrl+M',
                displayKey: 'Ctrl+M',
                description: 'Show drug/medication notes modal',
                scope: 'Drug Notes',
                category: 'Page Actions',
            },
            {
                key: 'Enter',
                displayKey: 'Enter',
                description: 'Finalize Verification. Approve if all checked, or open the Reject modal.',
                scope: 'Verification',
                category: 'Page Actions',
            },
            {
                key: 'KeyV',
                displayKey: 'V',
                description: 'Explicitly Verify Section when explicit verification is required',
                scope: 'Verification',
                category: 'Page Actions',
            },
        ]
    }
    private pageSpecificHotkeys: { [key: string]: Hotkey[] } = {
        'Audit': this.verificationHotkeys,
        'PV1': this.verificationHotkeys,
        'PV2': this.verificationHotkeys,
        'PrescriptionVerification': this.verificationHotkeys,
        'PrescriptionDetails': [
            {
                key: 'F5',
                displayKey: 'F5',
                description: 'Open prescription history',
                scope: 'Prescription Details',
                category: 'Page Actions',
            },
            {
                key: 'Alt+P',
                displayKey: 'Alt+P',
                description: 'Focus patient search',
                scope: 'Prescription Details',
                category: 'Navigation',
            },
            {
                key: 'Alt+R',
                displayKey: 'Alt+R',
                description: 'Focus prescriber search',
                scope: 'Prescription Details',
                category: 'Navigation',
            },
            {
                key: 'Alt+D',
                displayKey: 'Alt+D',
                description: 'Focus drug search',
                scope: 'Prescription Details',
                category: 'Navigation',
            },
            {
                key: 'Alt+Shift+D',
                displayKey: 'Alt+Shift+D',
                description: 'Find equivalent drugs',
                scope: 'Prescription Details',
                category: 'Page Actions',
            },
            {
                key: 'Alt+M',
                displayKey: 'Alt+M',
                description: 'Focus program search',
                scope: 'Prescription Details',
                category: 'Navigation',
            },
            {
                key: 'Alt+I',
                displayKey: 'Alt+I',
                description: 'Focus directions field',
                scope: 'Prescription Details',
                category: 'Navigation',
            },
            {
                key: 'Alt+O',
                displayKey: 'Alt+O',
                description: 'Focus origin field',
                scope: 'Prescription Details',
                category: 'Navigation',
            },
            {
                key: 'Alt+W',
                displayKey: 'Alt+W',
                description: 'Focus DAW (Dispense As Written) field',
                scope: 'Prescription Details',
                category: 'Navigation',
            },
            {
                key: 'Alt+B',
                displayKey: 'Alt+B',
                description: 'Focus insurance billing section',
                scope: 'Prescription Details',
                category: 'Navigation',
            },
            {
                key: 'Alt+L',
                displayKey: 'Alt+L',
                description: 'Show insurance selection',
                scope: 'Insurance Billing',
                category: 'Navigation',
            },
            {
                key: 'Ctrl+P',
                displayKey: 'Ctrl+P',
                description: 'Show patient notes modal',
                scope: 'Patient Notes',
                category: 'Page Actions',
            },
            {
                key: 'Ctrl+D',
                displayKey: 'Ctrl+D',
                description: 'Show doctor notes modal',
                scope: 'Doctor Notes',
                category: 'Page Actions',
            },
            {
                key: 'Ctrl+M',
                displayKey: 'Ctrl+M',
                description: 'Show drug/medication notes modal',
                scope: 'Drug Notes',
                category: 'Page Actions',
            },
            {
                key: 'Ctrl+R',
                displayKey: 'Ctrl+R',
                description: 'Reverse claim',
                scope: 'Insurance Billing',
                category: 'Page Actions',
            },
            {
                key: 'Ctrl+B',
                displayKey: 'Ctrl+B',
                description: 'Bill insurance',
                scope: 'Insurance Billing',
                category: 'Page Actions',
            },
        ],
        'DoctorFollowUp': [
            {
                key: 'F5',
                displayKey: 'F5',
                description: 'Open patient history',
                scope: 'Doctor Follow Up',
                category: 'Page Actions',
            },
        ],
    };

    mounted() {
        this.discoverHotkeys();
        document.addEventListener('keyup', this.globalHotkeyListener);
    }

    beforeDestroy() {
        document.removeEventListener('keyup', this.globalHotkeyListener);
    }

    private globalHotkeyListener(e: KeyboardEvent) {
        // Show modal on ? or Shift+/
        if (e.key === '?' || (e.shiftKey && e.key === '/')) {
            if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) return;

            e.preventDefault();
            this.show();
        }
    }

    private discoverHotkeys() {
        // Start with global hotkeys
        this.registeredHotkeys = [...this.globalHotkeys];

        // Check current route/component for page-specific hotkeys
        const currentComponent = this.$route.name || this.$route.path;
        // console.log("currentComponent: ", currentComponent, " pageSpecificHotkeys: ", this.pageSpecificHotkeys, " registeredHotkeys: ", this.registeredHotkeys, "");

        if (this.pageSpecificHotkeys[currentComponent]) {
            this.registeredHotkeys.push(...this.pageSpecificHotkeys[currentComponent]);
        }

        // Check if current page component has specific hotkeys
        if (this.$parent && this.$parent.$options.name) {
            const parentName = this.$parent.$options.name;
            if (this.pageSpecificHotkeys[parentName]) {
                this.registeredHotkeys.push(...this.pageSpecificHotkeys[parentName]);
            }
        }

        // Also check for any parent components up the chain
        let parent = this.$parent;
        while (parent) {
            // console.log("parent: ", parent.$options.name, " pageSpecificHotkeys: ", this.pageSpecificHotkeys, " registeredHotkeys: ", this.registeredHotkeys, "");
            if (parent.$options.name && this.pageSpecificHotkeys[parent.$options.name]) {
                const hotkeys = this.pageSpecificHotkeys[parent.$options.name];
                // Avoid duplicates
                hotkeys.forEach(hk => {
                    if (!this.registeredHotkeys.find(h => h.key === hk.key && h.scope === hk.scope)) {
                        this.registeredHotkeys.push(hk);
                    }
                });
            }
            parent = parent.$parent;
        }

        // Check for components that use HistoryHotkeyMixin
        if (this.hasHistoryMixin()) {
            // F8 is already in global hotkeys, just ensure it's there
            const f8Hotkey = this.registeredHotkeys.find(h => h.key === 'F8');
            if (f8Hotkey) {
                f8Hotkey.scope = 'Current Page';
            }
        }
    }

    private hasHistoryMixin(): boolean {
        // Check if any parent component uses HistoryHotkeyMixin
        let parent = this.$parent;
        while (parent) {
            if (parent.$options.mixins && parent.$options.mixins.some((m: any) => m.name === 'HistoryHotkeyMixin')) {
                return true;
            }
            // Also check if showHistoryModal property exists
            if ('showHistoryModal' in parent) {
                return true;
            }
            parent = parent.$parent;
        }
        return false;
    }

    get groupedHotkeys(): HotkeySection[] {
        const groups: { [key: string]: Hotkey[] } = {};

        this.registeredHotkeys.forEach(hotkey => {
            if (!groups[hotkey.category]) {
                groups[hotkey.category] = [];
            }
            groups[hotkey.category].push(hotkey);
        });

        // Convert to array and sort categories
        const categoryOrder = ['Navigation', 'Page Actions'];
        return Object.keys(groups)
            .sort((a, b) => {
                const aIndex = categoryOrder.indexOf(a);
                const bIndex = categoryOrder.indexOf(b);
                if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
                if (aIndex === -1) return 1;
                if (bIndex === -1) return -1;
                return aIndex - bIndex;
            })
            .map(category => ({
                category,
                items: groups[category],
            }));
    }

    public show() {
        this.discoverHotkeys(); // Refresh hotkeys when showing
        this.$bvModal.show(this.modalId);
    }

    public hide() {
        this.$bvModal.hide(this.modalId);
    }
}
</script>

<style scoped>
.section-title {
    color: #333;
    font-weight: 600;
    margin-bottom: 0.5rem;
    padding-bottom: 0.25rem;
    border-bottom: 1px solid #e9ecef;
}

.hotkey-key-cell {
    width: 120px;
    white-space: nowrap;
}

.hotkey-badge {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1;
    color: #495057;
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
}

.hotkey-description {
    padding-left: 1rem;
}

.hotkey-scope {
    text-align: right;
    width: 150px;
}

.table {
    margin-bottom: 0.5rem;
}

.table td {
    padding: 0.0rem 0.0rem;
    vertical-align: middle;
    border-top: 1px solid #f0f0f0;
}

.table tbody tr:first-child td {
    border-top: none;
}

kbd {
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}
</style>
